
import React from 'react';
import { hasDeputy } from '../services/projectRepresentatives';
import { ProjectLock } from '../services/projectLockLogic';
import { ProjectLockHint } from './ProjectLockHint';
import { istSifaProjekt } from '../services/projectType';
import type { ListProject } from '../services/listProject';

interface ProjectListItemProps {
    project: ListProject;
    onClick: (project: ListProject) => void;
    formatDate: (date?: string | null) => string;
    /** Wer sitzt gerade in diesem Projekt? Fehlt der Wert, entfaellt der Hinweis. */
    lock?: ProjectLock | null;
    currentUserId?: string | null;
    lockNowMs?: number;
}

export const ProjectListItem: React.FC<ProjectListItemProps> = ({ project, onClick, formatDate, lock, currentUserId, lockNowMs }) => {
    // Prefer SQL columns
    const status = project.status || project.data?.metadata?.globalData?.status || 'planning';
    const name = project.name;
    const number = project.project_number || project.data?.metadata?.projectNumber;
    const lastDate = project.last_inspection_date || project.data?.metadata?.date;

    // SiFa-Dauerbetreuung und berichtsbefreite Projekte kennen keinen
    // Begehungsturnus — die 21-Tage-Regel gilt dort nicht.
    const isSifa = istSifaProjekt(project);
    const reportExempt = project.data?.metadata?.globalData?.reportExempt === true;

    // Check if last inspection was more than 3 weeks ago (21 days)
    const isLongOverdue = React.useMemo(() => {
        if (status !== 'active' || !lastDate || isSifa || reportExempt) return false;
        const last = new Date(lastDate).getTime();
        const now = new Date().getTime();
        const diffDays = (now - last) / (1000 * 60 * 60 * 24);
        return diffDays > 21;
    }, [status, lastDate, isSifa, reportExempt]);

    const statusLabel = { 'planning': 'Planung', 'active': isSifa ? 'Laufend' : 'Ausführung', 'on_hold': 'Pausiert', 'completed': 'Beendet', 'finished': 'Archiv' }[status as string] || status;
    let statusColor = { 'planning': 'bg-blue-100 text-blue-700', 'active': 'bg-emerald-100 text-emerald-700', 'on_hold': 'bg-orange-100 text-orange-700', 'completed': 'bg-purple-100 text-purple-700', 'finished': 'bg-slate-100 text-slate-600' }[status as string] || 'bg-slate-100 text-slate-600';

    if (isLongOverdue) {
        statusColor = 'bg-red-100 text-red-700 animate-pulse border border-red-200';
    }

    const isJoint = hasDeputy(project.data?.metadata);

    return (
        <tr 
            onClick={() => onClick(project)} 
            className={`hover:bg-slate-50 cursor-pointer transition-colors group ${isLongOverdue ? 'bg-red-50/30' : ''}`}
        >
            <td className="px-6 py-4">
                <div className="flex flex-col gap-1 items-start">
                    <span className={`px-2 py-1 rounded text-[11px] font-bold uppercase tracking-wider ${statusColor}`}>
                        {isLongOverdue ? 'Überfällig' : statusLabel}
                    </span>
                    {isJoint && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-indigo-50 text-indigo-700 border border-indigo-100">Gemeinsam</span>}
                </div>
            </td>
            <td className="px-6 py-4">
                <div className="font-bold text-slate-800 group-hover:text-blue-600 transition-colors">{name}</div>
                {number && <div className="text-xs text-slate-500 font-mono mt-0.5">{number}</div>}
                <ProjectLockHint lock={lock} currentUserId={currentUserId} nowMs={lockNowMs || 0} className="mt-1" />
            </td>
            <td className="px-6 py-4 text-slate-600">
                {formatDate(lastDate)}
            </td>
            <td className="px-6 py-4 text-slate-500 font-mono text-xs">
                {formatDate(project.updated_at)}
            </td>
            <td className="px-6 py-4 text-right">
                <button className="text-xs font-bold text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded transition-colors opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100">
                    Öffnen
                </button>
            </td>
        </tr>
    );
};
